/**
 * plain-errors.ts — maps technical errors to calm, plain-language messages.
 *
 * Rules (mirrored in docs/dev-guides/error-message-style-guide.md):
 *  1. Always state what is safe ("Your work is saved").
 *  2. Always offer a concrete next action.
 *  3. No jargon, HTTP status codes, or stack traces visible to the user.
 *  4. No blame — don't say "you did X wrong".
 *  5. Warm, not alarming tone.
 */

import { ApiError } from "@teamblob/api-client";

/* ──────────────────────────────────────────────────────────────────
 * Output shape
 * ────────────────────────────────────────────────────────────────── */

export interface PlainError {
  /** Short headline shown prominently. */
  title: string;
  /** One to two sentences of context. Always states what's safe. */
  body: string;
  /** What the user can do right now. Shown as a CTA label. */
  nextAction?: string;
}

/* ──────────────────────────────────────────────────────────────────
 * Internal helpers
 * ────────────────────────────────────────────────────────────────── */

/** Pulls the first field-level validation message from ApiError.details. */
function firstValidationMessage(err: ApiError): string | null {
  const first = err.details?.[0];
  if (!first) return null;
  // Capitalise and ensure it ends with a period.
  const msg = first.message.trim();
  return msg.endsWith(".") ? msg : `${msg}.`;
}

/* ──────────────────────────────────────────────────────────────────
 * Main mapping function
 * ────────────────────────────────────────────────────────────────── */

/**
 * Converts any thrown value into a plain-language error descriptor.
 *
 * @example
 * try {
 *   await api.doSomething();
 * } catch (err) {
 *   const plain = toPlainError(err);
 *   showErrorToast(plain);
 * }
 */
export function toPlainError(err: unknown): PlainError {
  // ── ApiError from @teamblob/api-client ──────────────────────────
  if (err instanceof ApiError) {
    // Network-level failure (server unreachable)
    if (err.isNetworkError) {
      return {
        title: "Can't reach the server right now",
        body: "Your work is saved locally. This is usually a brief connection hiccup — try again in a moment.",
        nextAction: "Try again",
      };
    }

    // 401 — session expired
    if (err.isUnauthorized) {
      return {
        title: "You've been signed out",
        body: "Your session expired. Sign back in to continue — your work is saved.",
        nextAction: "Sign in",
      };
    }

    // 403 — not permitted
    if (err.isForbidden) {
      return {
        title: "You don't have access to that",
        body: "Your account doesn't have permission for this action. Contact your workspace admin if you think this is a mistake.",
        nextAction: "Go back",
      };
    }

    // 422 / 400 — validation
    if (err.isValidation) {
      const detail = firstValidationMessage(err);
      return {
        title: "Something looks off",
        body: detail
          ? `Please check: ${detail} Your other changes are still here.`
          : "One or more fields need attention. Your other changes are still here.",
        nextAction: "Review the form",
      };
    }

    // 404
    if (err.statusCode === 404) {
      return {
        title: "That item isn't here",
        body: "It may have been deleted or moved. Your other work is unaffected.",
        nextAction: "Go back",
      };
    }

    // 409 — conflict
    if (err.statusCode === 409) {
      return {
        title: "There's a conflict",
        body: "Someone else may have updated this at the same time. Reload to see the latest version — your draft is still here.",
        nextAction: "Reload",
      };
    }

    // 429 — rate limited
    if (err.statusCode === 429) {
      return {
        title: "Slow down for a moment",
        body: "Too many requests were sent in a short time. Wait a few seconds and try again.",
        nextAction: "Try again",
      };
    }

    // 5xx — server error
    if (err.statusCode >= 500) {
      return {
        title: "Something went wrong on our end",
        body: "This isn't your fault. Our team has been notified. Your work is saved — try again in a moment.",
        nextAction: "Try again",
      };
    }

    // Generic ApiError fallback
    return {
      title: "Something didn't work",
      body: "Your work is saved. Try the action again, or reload the page if the problem continues.",
      nextAction: "Try again",
    };
  }

  // ── Native network / fetch errors ───────────────────────────────
  if (err instanceof TypeError && err.message.toLowerCase().includes("fetch")) {
    return {
      title: "Connection problem",
      body: "Check your internet connection and try again. Your work is saved.",
      nextAction: "Try again",
    };
  }

  // ── Generic Error ────────────────────────────────────────────────
  if (err instanceof Error) {
    return {
      title: "Something went wrong",
      body: "Your work is saved. Try the action again, or reload the page if the problem continues.",
      nextAction: "Try again",
    };
  }

  // ── Unknown (string, object, etc.) ──────────────────────────────
  return {
    title: "Something went wrong",
    body: "Your work is saved. Try the action again, or reload the page if the problem continues.",
    nextAction: "Try again",
  };
}
